import React from 'react';
import SEO from '../components/SEO';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-page">
      <SEO 
        title="Privacy Policy - AppShow | Data Protection & Privacy Rights"
        description="Learn how AppShow protects your privacy and personal data. Comprehensive privacy policy covering data collection, storage, security, and your privacy rights for all our apps."
        keywords="privacy policy, data protection, privacy rights, GDPR compliance, data security, personal information, mobile app privacy"
        url="/privacy"
      />
      <div className="container">
        <header className="policy-header">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: January 15, 2025</p>
        </header>

        <div className="policy-content">
          <section className="policy-section">
            <h2>1. Introduction</h2>
            <p>
              This Privacy Policy describes how Helie (Sole Proprietor) ("we," "us," or "our") collects, uses, and protects your information when you use our mobile applications and services. We are committed to protecting your privacy and ensuring the security of your personal information.
            </p>
            <p>
              By using our applications, you agree to the collection and use of information in accordance with this Privacy Policy.
            </p>
          </section>

          <section className="policy-section">
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Personal Information</h3>
            <p>
              We may collect the following types of personal information:
            </p>
            <ul>
              <li>Contact information (email address when you contact us for support)</li>
              <li>Device information (device model, operating system version, unique device identifiers)</li>
              <li>Usage data (how you interact with our applications)</li>
              <li>Crash reports and performance data</li>
            </ul>

            <h3>2.2 Application-Specific Data</h3>
            <p>
              Depending on the specific application you use, we may collect:
            </p>
            <ul>
              <li><strong>SexLife Tracker:</strong> All intimate tracking data is stored locally on your device and is never transmitted to our servers or any third parties</li>
              <li>User preferences and settings</li>
              <li>In-app purchase information (processed by Apple/Google)</li>
            </ul>

            <h3>2.3 Automatically Collected Information</h3>
            <p>
              When you use our applications, we may automatically collect:
            </p>
            <ul>
              <li>Log data (IP address, browser type, pages visited)</li>
              <li>Analytics data (app usage patterns, feature utilization)</li>
              <li>Performance metrics (app crashes, load times)</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>3. How We Use Your Information</h2>
            <p>
              We use the collected information for the following purposes:
            </p>
            <ul>
              <li>To provide and maintain our applications</li>
              <li>To improve user experience and app functionality</li>
              <li>To provide customer support and respond to inquiries</li>
              <li>To detect, prevent, and address technical issues</li>
              <li>To analyze usage patterns and improve our services</li>
              <li>To comply with legal obligations</li>
              <li>To protect our rights and prevent fraud</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>4. Data Storage and Security</h2>
            
            <h3>4.1 Local Data Storage</h3>
            <p>
              Most of our applications, particularly SexLife Tracker, store data locally on your device. This means:
            </p>
            <ul>
              <li>Your personal data remains on your device</li>
              <li>We do not have access to your locally stored data</li>
              <li>Data is not transmitted to our servers</li>
              <li>You have full control over your data</li>
            </ul>

            <h3>4.2 Security Measures</h3>
            <p>
              We implement appropriate security measures to protect your information:
            </p>
            <ul>
              <li>Encryption of sensitive data</li>
              <li>Secure data transmission protocols</li>
              <li>Regular security assessments</li>
              <li>Limited access to personal information</li>
              <li>Secure server infrastructure</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>5. Data Sharing and Disclosure</h2>
            
            <h3>5.1 Third-Party Services</h3>
            <p>
              We may use third-party services that collect, monitor, and analyze data:
            </p>
            <ul>
              <li><strong>Google Analytics:</strong> For usage analytics and app performance</li>
              <li><strong>Firebase:</strong> For crash reporting and performance monitoring</li>
              <li><strong>App Store Platforms:</strong> Apple App Store and Google Play Store for app distribution</li>
            </ul>

            <h3>5.2 Legal Requirements</h3>
            <p>
              We may disclose your information if required by law or in response to:
            </p>
            <ul>
              <li>Legal processes or government requests</li>
              <li>Protection of our rights and property</li>
              <li>Prevention of fraud or illegal activities</li>
              <li>Protection of user safety</li>
            </ul>

            <h3>5.3 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.
            </p>
          </section>

          <section className="policy-section">
            <h2>6. Your Privacy Rights</h2>
            
            <h3>6.1 Access and Control</h3>
            <p>
              You have the right to:
            </p>
            <ul>
              <li>Access your personal information</li>
              <li>Update or correct your information</li>
              <li>Delete your account and associated data</li>
              <li>Opt-out of data collection where applicable</li>
              <li>Request data portability</li>
            </ul>

            <h3>6.2 Data Deletion</h3>
            <p>
              You can delete your data by:
            </p>
            <ul>
              <li>Uninstalling the application (for locally stored data)</li>
              <li>Contacting us at roboencode@gmail.com for server-stored data</li>
              <li>Using in-app data deletion features where available</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>7. Children's Privacy</h2>
            <p>
              Our applications are not intended for children under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
            </p>
            <p>
              Some of our applications, such as SexLife Tracker, are specifically designed for adults (18+) and contain mature content.
            </p>
          </section>

          <section className="policy-section">
            <h2>8. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and that your information receives adequate protection.
            </p>
          </section>

          <section className="policy-section">
            <h2>9. Data Retention</h2>
            <p>
              We retain your information for as long as necessary to:
            </p>
            <ul>
              <li>Provide our services</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes</li>
              <li>Enforce our agreements</li>
            </ul>
            <p>
              Locally stored data is retained until you delete the application or manually remove the data.
            </p>
          </section>

          <section className="policy-section">
            <h2>10. Cookies and Tracking Technologies</h2>
            <p>
              Our applications may use cookies and similar tracking technologies to:
            </p>
            <ul>
              <li>Remember user preferences</li>
              <li>Analyze app usage</li>
              <li>Improve functionality</li>
              <li>Provide personalized experiences</li>
            </ul>
            <p>
              You can control cookie preferences through your device settings.
            </p>
          </section>

          <section className="policy-section">
            <h2>11. Updates to Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by:
            </p>
            <ul>
              <li>Posting the new Privacy Policy on this page</li>
              <li>Updating the "Last updated" date</li>
              <li>Sending in-app notifications for significant changes</li>
              <li>Emailing registered users when applicable</li>
            </ul>
            <p>
              Your continued use of our applications after changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section className="policy-section">
            <h2>12. Contact Information</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> roboencode@gmail.com</p>
              <p><strong>Business Name:</strong> Helie (Sole Proprietor)</p>
              <p><strong>Response Time:</strong> We aim to respond to all inquiries within 48 hours</p>
            </div>
          </section>

          <section className="policy-section">
            <h2>13. Compliance</h2>
            <p>
              This Privacy Policy is designed to comply with:
            </p>
            <ul>
              <li>General Data Protection Regulation (GDPR)</li>
              <li>California Consumer Privacy Act (CCPA)</li>
              <li>Children's Online Privacy Protection Act (COPPA)</li>
              <li>Google Play Store policies</li>
              <li>Apple App Store guidelines</li>
              <li>Other applicable privacy laws and regulations</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>14. Specific App Policies</h2>
            
            <h3>14.1 SexLife Tracker</h3>
            <p>
              For our SexLife Tracker application, additional privacy protections apply:
            </p>
            <ul>
              <li>All intimate data is stored locally on your device only</li>
              <li>No intimate data is transmitted to any servers</li>
              <li>Passcode protection available for premium users</li>
              <li>Complete anonymity guaranteed</li>
              <li>No account creation required</li>
              <li>Data can be completely removed by uninstalling the app</li>
            </ul>
          </section>

          <div className="policy-footer">
            <p>
              This Privacy Policy is effective as of the date stated above and will remain in effect except with respect to any changes in its provisions in the future, which will be in effect immediately after being posted on this page.
            </p>
            <p>
              <strong>Last reviewed and updated:</strong> January 15, 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;


